import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { PublicUserEntity } from './public-user.entity';
import { tinyintBoolean } from './tinyint-boolean.transformer';

/**
 * PD-16475 — a storefront user's channel choices for one event.
 *
 * One row per (user, notificationType), written only when the user saves a
 * choice. No row means the defaults in `defaultPortalNotificationChannels`
 * apply, so the defaults can follow the account (an email address, a phone,
 * a subscribed browser) instead of being frozen at signup.
 *
 * A saved `smsEnabled` is the user's wish, not permission: a text also needs
 * recorded consent for the current phone and no STOP (see
 * `public_user_notification_setting`).
 */
@Index(
	'public_user_notification_preference_user_type_idx',
	['fkPublicUserId', 'notificationType'],
	{ unique: true },
)
@Entity('public_user_notification_preference')
export class PublicUserNotificationPreferenceEntity extends BaseEntity {
	@Column('int', { name: 'fk_public_user__id', unsigned: true })
	public fkPublicUserId!: number;

	@Column('varchar', { name: 'notificationType', length: 64 })
	public notificationType!: string;

	@Column('tinyint', {
		name: 'inAppEnabled',
		width: 1,
		default: () => "'1'",
		transformer: tinyintBoolean,
	})
	public inAppEnabled!: boolean;

	@Column('tinyint', {
		name: 'emailEnabled',
		width: 1,
		default: () => "'1'",
		transformer: tinyintBoolean,
	})
	public emailEnabled!: boolean;

	@Column('tinyint', {
		name: 'smsEnabled',
		width: 1,
		default: () => "'0'",
		transformer: tinyintBoolean,
	})
	public smsEnabled!: boolean;

	/** Browser push. Native app push is a later column (PD-14302). */
	@Column('tinyint', {
		name: 'pushEnabled',
		width: 1,
		default: () => "'0'",
		transformer: tinyintBoolean,
	})
	public pushEnabled!: boolean;

	@ManyToOne(() => PublicUserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_public_user__id', referencedColumnName: 'id' }])
	public fkPublicUser!: PublicUserEntity;
}
